import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { AuthContext } from "./auth";
import { listWalletTokens } from "./walletTokens";

/** Per-page fetch ceiling for the directory build. Loops until exhausted.
 * Must not exceed the API's listQuerySchema pageSize .max(200) — 500 made
 * every directory fetch 400 and counterparty names never resolved. */
const PAGE_SIZE = 200;
/** Defensive upper bound — prevents a runaway loop if the API miscounts. */
const MAX_PAGES = 20;

/**
 * Client-side resolver that turns a `(chainId, address)` pair into the matching
 * wallet name when the counterparty is one of the user's own active wallets.
 *
 * Why a context (and not a one-off fetch per row): a typical account holds
 * tens to a few hundred wallet tokens, but a single transactions page can
 * render hundreds of rows. We fetch the directory once on mount inside the
 * authed shell and expose a synchronous `lookup` so render hot-paths don't
 * each issue their own request.
 *
 * Match key: `${chainId}:${address.toLowerCase()}`. Wallet rows and tx
 * counterparties both flow through the same `ChainAdapter.validateAddress`
 * pipeline server-side, so they share canonical form. Lowercasing both sides
 * is a no-op for matching purposes and immunizes the lookup against future
 * drift in EIP-55 casing.
 */

export interface WalletDirectoryValue {
  /** True once the initial fetch has resolved (success or failure). */
  ready: boolean;
  /**
   * Resolve a `(chainId, address)` pair to the wallet's name. Returns `null`
   * when no match (including: unknown address, chain mismatch, empty input,
   * not-yet-loaded directory).
   */
  lookup: (chainId: string, address: string | null | undefined) => string | null;
  /** Re-fetch the directory — call after wallet create / patch / delete. */
  refresh: () => Promise<void>;
}

const EMPTY_VALUE: WalletDirectoryValue = {
  ready: false,
  lookup: () => null,
  refresh: async () => {},
};

export const WalletDirectoryContext =
  createContext<WalletDirectoryValue>(EMPTY_VALUE);

function buildKey(chainId: string, address: string): string {
  return `${chainId}:${address.toLowerCase()}`;
}

export interface WalletDirectoryProviderProps {
  children: ReactNode;
}

export function WalletDirectoryProvider({
  children,
}: WalletDirectoryProviderProps) {
  // Read auth tolerantly so the provider can also be exercised in unit tests
  // without mounting AuthProvider. When the context is absent we fall back
  // to a sentinel scope key — the production app always renders inside
  // AuthProvider, so the gating below still kicks in for real users.
  const auth = useContext(AuthContext);
  const accountScopeKey: string = auth ? auth.activeAccountId ?? "" : "__test__";
  const isAuthenticated = auth ? !!auth.activeAccountId : true;
  const [map, setMap] = useState<Map<string, string>>(() => new Map());
  const [ready, setReady] = useState(false);
  // Track active fetch so we can ignore stale responses on unmount/refresh
  // and on rapid account switches.
  const inflightRef = useRef(0);

  const fetchAll = useCallback(async () => {
    const ticket = ++inflightRef.current;
    try {
      // Page through the wallet list until exhausted. The default page
      // ceiling is 500; we guard with MAX_PAGES so a misbehaving API can't
      // pin a session in an infinite loop.
      const next = new Map<string, string>();
      for (let page = 1; page <= MAX_PAGES; page++) {
        const res = await listWalletTokens({
          status: "active",
          page,
          pageSize: PAGE_SIZE,
        });
        if (ticket !== inflightRef.current) return;
        for (const item of res.items) {
          if (!item.address) continue;
          next.set(buildKey(item.chainId, item.address), item.name);
        }
        if (res.items.length < PAGE_SIZE) break;
      }
      if (ticket !== inflightRef.current) return;
      setMap(next);
    } catch {
      // Don't blow up the UI when the directory call fails — counterparty
      // cells gracefully fall back to the truncated address. This is the
      // same defensive posture as `Address` in `ui/Address.tsx`.
      if (ticket === inflightRef.current) setMap(new Map());
    } finally {
      if (ticket === inflightRef.current) setReady(true);
    }
  }, []);

  useEffect(() => {
    // Re-fetch on account switch. The user can flip accounts in-session via
    // <AccountSwitcher>, which mutates `activeAccountId` without unmounting
    // the AppShell — without this dep, lookups would resolve against the
    // previous account's wallets and leak counterparty names cross-tenant.
    // Clear immediately so any render between the switch and the new fetch
    // shows the safe truncated-address fallback rather than stale matches.
    inflightRef.current++;
    setMap(new Map());
    setReady(false);
    if (!isAuthenticated) return;
    void fetchAll();
  }, [accountScopeKey, isAuthenticated, fetchAll]);

  const lookup = useCallback<WalletDirectoryValue["lookup"]>(
    (chainId, address) => {
      if (!address) return null;
      return map.get(buildKey(chainId, address)) ?? null;
    },
    [map],
  );

  const value = useMemo<WalletDirectoryValue>(
    () => ({ ready, lookup, refresh: fetchAll }),
    [ready, lookup, fetchAll],
  );

  return (
    <WalletDirectoryContext.Provider value={value}>
      {children}
    </WalletDirectoryContext.Provider>
  );
}

/**
 * Read access to the wallet directory. Always safe to call even outside a
 * provider — falls back to a no-op resolver so unauthenticated surfaces and
 * legacy tests don't have to mount the provider just to render a pill.
 */
export function useWalletDirectory(): WalletDirectoryValue {
  return useContext(WalletDirectoryContext);
}
